/**
 * Sección "Preferencias" — locale + timezone.
 *
 * Lista corta de locales y timezones relevantes para Colombia. Si en
 * el futuro abrimos a más países, generamos desde Intl.supportedValuesOf.
 */

import { useState } from 'react'
import { toast } from 'sonner'

import {
  ActionButton,
  FieldRow,
  SelectInput,
  SettingsSection,
} from '@/components/ui/settings-primitives'
import { useUpdatePreferences } from '@/hooks/api/use-profile'
import type { ProfileResponse } from '@/lib/api'

const LOCALES = [
  { value: 'es-CO', label: 'Español (Colombia)' },
  { value: 'es-MX', label: 'Español (México)' },
  { value: 'es-ES', label: 'Español (España)' },
  { value: 'en-US', label: 'English (US)' },
]

const TIMEZONES = [
  { value: 'America/Bogota', label: 'America/Bogota (UTC−5)' },
  { value: 'America/Mexico_City', label: 'America/Mexico_City (UTC−6)' },
  { value: 'America/Lima', label: 'America/Lima (UTC−5)' },
  { value: 'America/New_York', label: 'America/New_York (UTC−5/−4)' },
  { value: 'Europe/Madrid', label: 'Europe/Madrid (UTC+1/+2)' },
  { value: 'UTC', label: 'UTC' },
]

export function PreferencesSection({ profile }: { profile: ProfileResponse }) {
  const [locale, setLocale] = useState(profile.preferred_locale ?? 'es-CO')
  const [tz, setTz] = useState(profile.preferred_timezone ?? 'America/Bogota')
  const dirty =
    locale !== (profile.preferred_locale ?? 'es-CO') ||
    tz !== (profile.preferred_timezone ?? 'America/Bogota')

  const update = useUpdatePreferences()

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await update.mutateAsync({
        preferred_locale: locale || null,
        preferred_timezone: tz || null,
      })
      toast.success('Preferencias actualizadas')
    } catch {
      toast.error('No pudimos guardar las preferencias')
    }
  }

  return (
    <SettingsSection
      title="Idioma y zona horaria"
      description="Determinan cómo se formatean fechas, horas y montos en el panel."
      footer={
        <ActionButton type="submit" form="prefs-form" disabled={!dirty} loading={update.isPending}>
          Guardar preferencias
        </ActionButton>
      }
    >
      <form id="prefs-form" onSubmit={onSubmit} className="grid gap-4 sm:grid-cols-2">
        <FieldRow label="Idioma">
          <SelectInput value={locale} onChange={(e) => setLocale(e.target.value)}>
            {LOCALES.map((l) => (
              <option key={l.value} value={l.value}>
                {l.label}
              </option>
            ))}
          </SelectInput>
        </FieldRow>
        <FieldRow label="Zona horaria">
          <SelectInput value={tz} onChange={(e) => setTz(e.target.value)}>
            {TIMEZONES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </SelectInput>
        </FieldRow>
      </form>
    </SettingsSection>
  )
}
